import { NewBookingAlert } from '@spike/new-booking-model';

export interface RoutedBookingAlerts {
    staff: Array<NewBookingAlert>;
    dateTime: Array<NewBookingAlert>;
    zone: Array<NewBookingAlert>;
    session: Array<NewBookingAlert>;
    sessionFull: Array<NewBookingAlert>;
    general: Array<NewBookingAlert>;
}

export const OUTSIDE_BUSINESS_HOURS_ALERT: NewBookingAlert = {
    alertType: 'date_time',
    message: 'Date/Time outside business hours',
    error: false
};

// Types the backend adds later land in `general` instead of being dropped, so a new
// alert reaches the user without a frontend release.
export const routeBookingAlerts = (alerts?: Array<NewBookingAlert>): RoutedBookingAlerts =>
    (alerts ?? []).reduce<RoutedBookingAlerts>(
        (routed, alert) => {
            switch (alert.alertType) {
                case 'staff':
                    routed.staff.push(alert);
                    break;
                case 'date_time':
                    routed.dateTime.push(alert);
                    break;
                case 'walking_zone':
                case 'training_zone':
                    routed.zone.push(alert);
                    break;
                case 'walking_no_session':
                case 'training_no_session':
                    routed.session.push(alert);
                    break;
                case 'walking_session_full':
                case 'training_session_full':
                    routed.sessionFull.push(alert);
                    break;
                default:
                    routed.general.push(alert);
            }
            return routed;
        },
        { staff: [], dateTime: [], zone: [], session: [], sessionFull: [], general: [] }
    );

const EMPTY_ROUTED: RoutedBookingAlerts = {
    staff: [],
    dateTime: [],
    zone: [],
    session: [],
    sessionFull: [],
    general: []
};

const alertKey = (alert: NewBookingAlert): string => `${alert.alertType}|${alert.message}`;

const uniqueAlerts = (alerts: Array<NewBookingAlert>): Array<NewBookingAlert> => {
    const seen = new Set<string>();
    return alerts.filter(alert => {
        const key = alertKey(alert);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

// Joins routed alerts from several checks, keeping each type + message once.
export const mergeRoutedAlerts = (...routed: Array<RoutedBookingAlerts>): RoutedBookingAlerts =>
    (Object.keys(EMPTY_ROUTED) as Array<keyof RoutedBookingAlerts>).reduce<RoutedBookingAlerts>(
        (merged, group) => ({ ...merged, [group]: uniqueAlerts(routed.flatMap(entry => entry[group])) }),
        EMPTY_ROUTED
    );

// Groups that can move from the cards to the form. Session full belongs to its card.
const HOISTABLE_GROUPS: Array<keyof RoutedBookingAlerts> = ['staff', 'dateTime', 'zone'];

// Several selected sessions are checked one by one, so an alert that does not depend on the
// session (a pet or client alert, or a zone every session misses) comes back on every card.
// Those are shown once on the form: `general` always, and a staff, date/time or zone alert
// when every checked session returned it. The rest stays on its card.
export const splitSessionAlerts = (
    bySlot: Record<string, RoutedBookingAlerts>
): { shared: RoutedBookingAlerts; bySlot: Record<string, RoutedBookingAlerts> } => {
    const slots = Object.values(bySlot);
    if (slots.length === 0) return { shared: EMPTY_ROUTED, bySlot };

    const inEverySlot = new Set(
        slots.length < 2
            ? []
            : HOISTABLE_GROUPS.flatMap(group =>
                  slots[0][group]
                      .map(alertKey)
                      .filter(key => slots.every(slot => slot[group].some(alert => alertKey(alert) === key)))
              )
    );

    const shared = mergeRoutedAlerts(
        ...slots.map(slot => ({
            ...EMPTY_ROUTED,
            general: slot.general,
            ...Object.fromEntries(
                HOISTABLE_GROUPS.map(group => [group, slot[group].filter(alert => inEverySlot.has(alertKey(alert)))])
            )
        }))
    );

    const remaining = Object.fromEntries(
        Object.entries(bySlot).map(([key, slot]) => [
            key,
            {
                ...slot,
                general: [],
                ...Object.fromEntries(
                    HOISTABLE_GROUPS.map(group => [
                        group,
                        slot[group].filter(alert => !inEverySlot.has(alertKey(alert)))
                    ])
                )
            } as RoutedBookingAlerts
        ])
    );

    return { shared, bySlot: remaining };
};
